import { useState } from "react";
import {
  useMonitors,
  useAlerts,
  useServiceMetrics,
} from "../hooks/useObservability";
import PageHeader from "../components/common/PageHeader";
import StatusBadge from "../components/common/StatusBadge";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";
import SearchInput from "../components/common/SearchInput";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";

const statusColors: Record<string, string> = {
  OK: "green",
  Alert: "red",
  Warn: "yellow",
  "No Data": "gray",
};

export default function Observability() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: monitors, isLoading, error, refetch } = useMonitors();
  const { data: alerts } = useAlerts({ page: 1, pageSize: 20 });

  const filteredMonitors = (monitors ?? []).filter((m) => {
    const matchesSearch =
      !search ||
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      (m.serviceSlug ?? "").toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = (monitors ?? []).reduce(
    (acc, m) => {
      acc[m.status] = (acc[m.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  if (isLoading) return <LoadingSpinner message="Loading monitors..." />;
  if (error)
    return <ErrorBanner message="Failed to load monitors" onRetry={refetch} />;

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="Observability"
        description="Datadog monitors, metrics, and alert history"
      />

      {/* Status Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {["OK", "Warn", "Alert", "No Data"].map((status) => (
          <button
            key={status}
            onClick={() =>
              setStatusFilter(statusFilter === status ? "all" : status)
            }
            className={`rounded-2xl border p-5 text-center transition-all duration-200 shadow-metric hover:shadow-metric-hover
              bg-white dark:bg-slate-900 dark:border-slate-800/80
              ${
                statusFilter === status
                  ? "border-emerald-500 ring-2 ring-emerald-200 dark:ring-emerald-500/30 dark:border-emerald-400 shadow-emerald-100 dark:shadow-none"
                  : "border-slate-200/60 dark:border-slate-800/80 hover:border-slate-200 dark:hover:border-slate-700"
              }`}
          >
            <div className="text-2xl font-bold text-slate-900 dark:text-white">
              {statusCounts[status] || 0}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              <StatusBadge
                status={statusColors[status] || "gray"}
                label={status}
              />
            </div>
          </button>
        ))}
      </div>

      {/* Monitors Table */}
      <div className="card">
        <div className="card-header flex items-center gap-4">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search monitors..."
          />
        </div>
        <div className="card-body p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-50 dark:bg-slate-800/50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              <tr>
                <th className="px-4 py-3">Monitor</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Last Sync</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredMonitors.map((monitor) => (
                <tr
                  key={monitor.id}
                  className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <a
                      href={monitor.dashboardUrl ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 font-medium"
                    >
                      {monitor.name}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {monitor.serviceSlug ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {monitor.type ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={statusColors[monitor.status] || "gray"}
                      label={monitor.status}
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {monitor.priority ? `P${monitor.priority}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-500 text-xs">
                    {monitor.syncedAt
                      ? format(new Date(monitor.syncedAt), "MMM d, HH:mm")
                      : "—"}
                  </td>
                </tr>
              ))}
              {filteredMonitors.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-slate-400 dark:text-slate-500"
                  >
                    No monitors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-card-title text-slate-900 dark:text-white">
            Recent Alerts
          </h3>
        </div>
        <div className="card-body p-0 divide-y divide-slate-100 dark:divide-slate-800">
          {(alerts?.items ?? []).slice(0, 10).map((alert) => (
            <div
              key={alert.id}
              className="px-4 py-3 flex items-center justify-between hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-2 h-2 rounded-full ${
                    alert.alertResolvedAt
                      ? "bg-green-500 dark:bg-green-400"
                      : "bg-red-500 dark:bg-red-400"
                  }`}
                />
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                    {alert.title}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {alert.serviceSlug ?? "Unknown service"} &middot;{" "}
                    {format(
                      new Date(alert.alertStartedAt),
                      "MMM d, HH:mm"
                    )}
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                {alert.durationSeconds
                  ? `${Math.round(alert.durationSeconds / 60)}m`
                  : "Ongoing"}
              </div>
            </div>
          ))}
          {(!alerts || alerts.items.length === 0) && (
            <div className="px-4 py-8 text-center text-slate-400 dark:text-slate-500">
              No recent alerts
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
